import {Component, inject, signal, WritableSignal} from '@angular/core';
import {BadgeModule} from 'primeng/badge';
import {CommonModule} from '@angular/common';
import {NavigationEnd, Router, RouterLink, RouterOutlet} from '@angular/router';
import {filter} from 'rxjs/operators';
import {forkJoin} from 'rxjs';
import {FormsModule} from '@angular/forms';
import {ButtonModule} from 'primeng/button';
import {AvatarModule} from 'primeng/avatar';
import {MenuModule} from 'primeng/menu';
import {RippleModule} from 'primeng/ripple';
import {PanelMenuModule} from 'primeng/panelmenu';
import {MenuItem} from 'primeng/api';
import { LayoutService } from '../../../services/layout.service';
import { DrawerModule } from 'primeng/drawer';
import * as packageJson from '../../../../../package.json'
import { DividerModule } from "primeng/divider";
import { UserService } from '../../../services/user.service';
import type { User } from '../../../interfaces/user';
import { RoleEnum } from '../../../interfaces/application';
import { UtmSegmentosService } from '../../../services/utm-segmentos.service';

@Component({
  selector: 'app-panel-layout',
  imports: [
    CommonModule,
    BadgeModule,
    RouterOutlet,
    FormsModule,
    ButtonModule,
    RouterLink,
    AvatarModule,
    MenuModule,
    RippleModule,
    PanelMenuModule,
    DrawerModule,
    DividerModule
],
  templateUrl: './panel-layout.html',
  styleUrl: './panel-layout.css'
})
export class PanelLayout {

  protected version = packageJson.version

  private readonly _router: Router = inject(Router);
  private readonly _layoutService: LayoutService = inject(LayoutService);
  private readonly _userService: UserService = inject(UserService);
  private readonly _utmSegmentosService: UtmSegmentosService = inject(UtmSegmentosService);

  public theme$ = this._layoutService.selectedTheme$;
  public showMenuDrawer$ = this._layoutService.showMenuDrawer$;

  public user: WritableSignal<User|null> = signal<User|null>(null);
  public currentRoute: WritableSignal<string> = signal<string>(this._router.url);

  public menuItems: MenuItem[] = [];
  private segmentosLoaded = false;

  constructor() {
    this._router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe({
        next: (event: NavigationEnd) => this.currentRoute.set(event.urlAfterRedirects)
      });
  }

  public ngOnInit(): void {
    this._getUser();
  }

  public isActiveRoute(route: string) {
    return this.currentRoute().endsWith(route);
  }

  public toggleMenuDrawer(state?: boolean): void {
    if (window.innerWidth < 1024) {
      this._layoutService.toggleMenuDrawer(state);
    }
  }

  private _getUser(): void {
    this._userService.me()
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.user.set(response.data);
            this._loadSegmentos();
          }
        },
      })
  }

  private _loadSegmentos(): void {
    forkJoin({
      market: this._utmSegmentosService.getSegmentosMarket(),
      telemarketing: this._utmSegmentosService.getSegmentosTelemarketing()
    }).subscribe({
      next: (responses: any) => {
        let segmentosMarket: any[] = [];
        let segmentosTelemarketing: any[] = [];

        // Process Market response - converter objeto para array
        if (responses.market?.success && responses.market.data) {
          segmentosMarket = Object.values(responses.market.data);
        } else if (responses.market?.data) {
          segmentosMarket = Array.isArray(responses.market.data)
            ? responses.market.data
            : Object.values(responses.market.data);
        } else if (Array.isArray(responses.market)) {
          segmentosMarket = responses.market;
        }

        // Process Telemarketing response - converter objeto para array
        if (responses.telemarketing?.success && responses.telemarketing.data) {
          segmentosTelemarketing = Object.values(responses.telemarketing.data);
        } else if (responses.telemarketing?.data) {
          segmentosTelemarketing = Array.isArray(responses.telemarketing.data)
            ? responses.telemarketing.data
            : Object.values(responses.telemarketing.data);
        } else if (Array.isArray(responses.telemarketing)) {
          segmentosTelemarketing = responses.telemarketing;
        }

        this.segmentosLoaded = true;
        this._initMenuItems(segmentosMarket, segmentosTelemarketing);
      },
      error: (err) => {
        console.error('Erro ao carregar segmentos:', err);
        this.segmentosLoaded = true;
        this._initMenuItems([], []);
      }
    });
  }

  private _userHasPermission(permission: string): boolean {
    return this.user()?.roles.some(role => role.name === RoleEnum.MASTER || role.permissions.some(p => p.name === permission)) || false;
  }

  private _userHasAnyPermission(permissions: string[]): boolean {
    return this.user()?.roles.some(role => role.name === RoleEnum.MASTER || role.permissions.some(p => permissions.includes(p.name))) || false;
  }

  private _userHasRole(roleName: RoleEnum): boolean {
    return this.user()?.roles.some(role => role.name === roleName) || false;
  }

  private _initMenuItems(segmentosMarket: any[] = [], segmentosTelemarketing: any[] = []): void {
    // Garantir que segmentos seja sempre um array
    if (!Array.isArray(segmentosMarket)) {
      segmentosMarket = [];
    }
    if (!Array.isArray(segmentosTelemarketing)) {
      segmentosTelemarketing = [];
    }

    // Create Market submenu items from segmentos
    const marketItems: MenuItem[] = [
      // Add Cohort as first item in Market menu
      {
        label: 'Cohort',
        icon: 'fa-solid fa-chart-column',
        command: () => this._router.navigate(['reports', 'cohort']),
        visible: this._userHasPermission('view reports cohort')
      },
      // Add segmentos
      ...segmentosMarket.map(seg => ({
        label: seg.descricao || seg.nome || seg.id,
        icon: 'fa-solid fa-tag',
        command: () => this._router.navigate(['market', seg.id]),
        visible: this._userHasPermission('get metrics')
      }))
    ];

    const telemarketingItems: MenuItem[] = [
      // Add segmentos
      ...segmentosTelemarketing.map(seg => ({
        label: seg.descricao || seg.nome || seg.id,
        icon: 'fa-solid fa-tag',
        command: () => this._router.navigate(['telemarketing', seg.id]),
        visible: this._userHasPermission('get metrics')
      }))
    ];


    this.menuItems = [
      {
        label: 'Dashboard',
        icon: 'fa-solid fa-grip',
        url: 'dashboard',
        command: () => this._router.navigate(['dashboard']),
        visible: this._userHasPermission('view dashboard')
      },
      {
        label: 'Relatórios e Análises',
        icon: 'fa-solid fa-chart-line',
        visible: !this._userHasRole(RoleEnum.AFFILIATE),
        items: [
          {
            label: 'Relatório de Depósitos',
            icon: 'fa-solid fa-money-bill-trend-up',
            command: () => this._router.navigate(['reports', 'deposits']),
            visible: !this._userHasRole(RoleEnum.AFFILIATE)
          }
        ],
      },
      {
        label: 'Market',
        icon: 'fa-solid fa-store',
        visible: (this._userHasPermission('view market') || this._userHasPermission('view reports cohort')),
        items: marketItems
      },
      {
        label: 'Telemarketing',
        icon: 'fa-solid fa-store',
        visible: this._userHasPermission('view telemarketing'),
        items: telemarketingItems
      },
      {
        label: 'Métricas Apostadores',
        icon: 'fa-solid fa-users',
        command: () => this._router.navigate(['utm-bettors']),
        visible: this._userHasPermission('get utm bettors')
      },
      {
        label: 'Extrato',
        icon: 'fa-solid fa-receipt',
        command: () => this._router.navigate(['statements']),
        visible: this._userHasPermission('view statements')
      },
      {
        label: 'Área de Pagamentos',
        icon: 'fa-solid fa-money-bill-wave',
        command: () => this._router.navigate(['payments']),
        visible: this._userHasPermission('view payments')
      },
      {
        label: 'Contratos',
        icon: 'fa-solid fa-scale-unbalanced-flip',
        command: () => this._router.navigate(['contracts']),
        visible: this._userHasPermission('view contracts')
      },
      {
        label: 'Comunicados',
        icon: 'fa-regular fa-message',
        command: () => this._router.navigate(['announcements']),
        visible: this._userHasPermission('view announcements')
      },
      {
        label: 'Usuários',
        icon: 'fa-regular fa-user',
        command: () => this._router.navigate(['users']),
        visible: this._userHasPermission('view users')
      },
      {
        label: 'Permissões',
        icon: 'fa-solid fa-user-lock',
        command: () => this._router.navigate(['permissions']),
        visible: this._userHasPermission('view roles')
      }
    ];

    // Force change detection by creating a new array reference
    this.menuItems = [...this.menuItems];
  }

  private _addDashboard(): void {
    this.menuItems.push({
      label: 'Dashboard',
      icon: 'fa-solid fa-grip',
      badge: '',
      url: 'dashboard',
      action: () => this._router.navigate(['dashboard']),
      items: []
    });
  }

  private _addStatements(): void {
    this.menuItems.push({
      label: 'Dashboard',
      icon: 'fa-solid fa-grip',
      badge: '',
      url: 'dashboard',
      action: () => this._router.navigate(['dashboard']),
      items: []
    });
  }

  private _addPayments(): void {
    this.menuItems.push({
      label: 'Dashboard',
      icon: 'fa-solid fa-grip',
      badge: '',
      url: 'dashboard',
      action: () => this._router.navigate(['dashboard']),
      items: []
    });
  }

  private _addContracts(): void {
    this.menuItems.push({
      label: 'Dashboard',
      icon: 'fa-solid fa-grip',
      badge: '',
      url: 'dashboard',
      action: () => this._router.navigate(['dashboard']),
      items: []
    });
  }

  private _addAnnouncements(): void {
    this.menuItems.push({
      label: 'Dashboard',
      icon: 'fa-solid fa-grip',
      badge: '',
      url: 'dashboard',
      action: () => this._router.navigate(['dashboard']),
      items: []
    });
  }

  private _addUsers(): void {
    this.menuItems.push({
      label: 'Dashboard',
      icon: 'fa-solid fa-grip',
      badge: '',
      url: 'dashboard',
      action: () => this._router.navigate(['dashboard']),
      items: []
    });
  }
}
